import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { UserAccount } from '../../models/user.model';
import { environment } from '../../../../../environments/environment';
import { AuthenticationRequest, AuthenticationResult, StsTokenOutput } from '../../models/auth.model';
import { PasswordComplexitySettings, ResetPasswordRequest, ResetPasswordResult } from '../../models/password.model';
import { GetCurrentLoginInformationsOutput } from '../../models/app-session.model';
import { IImpersonatedAuthenticationResponseModelData, ImpersonateInput, ImpersonateOutput } from '../../models/impersonation.model';
import { BYPASS_LOG } from '../interceptors/auth.interceptor';

const API_BASE_URL = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  constructor(private http: HttpClient) { }

  // public methods
  login(auth: AuthenticationRequest): Observable<AuthenticationResult> {
    let url = API_BASE_URL + "api/TokenAuth/Authenticate";
    url = url.replace(/[?&]$/, "");

    return this.http.post(url, auth)
      .pipe(
        map((response: any) => {
          if (!response.result) {
            throw new Error("Failed to authenticate");
          }

          return response.result;
        })
      );
  }

  requestEmailActivation(email: string): Observable<void> {
    let url = API_BASE_URL + "api/services/app/Account/SendEmailActivationLink";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, { emailAddress: email });
  }

  activateEmail(code: string): Observable<void> {
    let url = API_BASE_URL + "api/services/app/Account/ActivateEmail";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, { c: code });
  }

  forgotPassword(email: string): Observable<void> {
    let url = API_BASE_URL + "api/services/app/Account/SendPasswordResetCode";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, { emailAddress: email });
  }

  resolveTenant(code: string): Observable<number> {
    let url = API_BASE_URL + "api/services/app/Account/ResolveTenantId";
    url = url.replace(/[?&]$/, "");

    return this.http.post<any>(url, { c: code })
      .pipe(
        map((response: any) => {
          if (response.result) {
            return +response.result;
          }

          return 0;
        })
      );
  }

  getPasswordComplexitySettings(): Observable<PasswordComplexitySettings> {
    let url = API_BASE_URL + "api/services/app/Profile/GetPasswordComplexitySetting";
    url = url.replace(/[?&]$/, "");

    return this.http.get<any>(url)
      .pipe(
        map((response: any) => {
          if (!response.result) {
            throw new Error("Failed to get security settings");
          }
          return response.result.setting;
        })
      );
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResult> {
    let url = API_BASE_URL + "api/services/app/Account/ResetPassword";
    url = url.replace(/[?&]$/, "");

    return this.http.post<ResetPasswordResult>(url, request)
      .pipe(
        map((response: any) => {
          if (!response.result)
            throw new Error("Failed to reset password. Please contact support.");

          return response.result;
        })
      );
  }

  getCurrentUserDetails(): Observable<UserAccount> {
    let url = API_BASE_URL + "api/services/app/Session/GetCurrentLoginInformations";
    url = url.replace(/[?&]$/, "");

    return this.http.get(url)
      .pipe(
        map((response: any) => {
          if (!response.result)
            throw new Error("Failed to get current user");

          return response.result.user;
        })
      );
  }

  getSessionData(): Observable<any> {
    let url = API_BASE_URL + "AbpUserConfiguration/GetAll";
    url = url.replace(/[?&]$/, "");

    return this.http.get(url)
      .pipe(
        map((response: any) => {
          if (!response.result)
            throw new Error("Failed to get session data.");

          return response.result;
        })
      );
  }

  getCurrentLoginInformations(): Observable<GetCurrentLoginInformationsOutput> {
    let url = API_BASE_URL + "api/services/app/Session/GetCurrentLoginInformations";
    url = url.replace(/[?&]$/, "");

    return this.http.get<GetCurrentLoginInformationsOutput>(url)
      .pipe(
        map((response: any) => {
          if (!response.result) {
            throw new Error("Failed to get current login information");
          }
          return response.result;
        })
      );
  }

  
  /* Impersonation  */
  impersonate(input: ImpersonateInput | null | undefined): Observable<ImpersonateOutput> {
    let url = API_BASE_URL + "api/services/app/Account/ImpersonateUser";
    url = url.replace(/[?&]$/, "");

    return this.http.post<ImpersonateOutput>(url, input).pipe(
      map((response: any) => {
        if (!response.result) {
          throw Error("Failed to impersonate user");
        }
        return response.result;
      })
    );
  }

  backToImpersonator(): Observable<ImpersonateOutput> {
    let url = API_BASE_URL + "api/services/app/Account/BackToImpersonator";
    url = url.replace(/[?&]$/, "");

    return this.http.post<ImpersonateOutput>(url, {}).pipe(
      map((response: any) => {
        if (!response.result) {
          throw Error("Failed to impersote user");
        }
        return response.result;
      })
    );
  }

  impersonatedLogin(token: string): Observable<IImpersonatedAuthenticationResponseModelData> {
    let url = API_BASE_URL + 'api/TokenAuth/ImpersonatedAuthenticate?impersonationToken=' + encodeURIComponent(token);
    url = url.replace(/[?&]$/, "");

    return this.http.post<IImpersonatedAuthenticationResponseModelData>(url, null);
  }

  callSts(azureToken: string): Observable<StsTokenOutput> {
    let postParams = new HttpParams({
      fromObject: {
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        audience: 'Stealth',
        scope: 'StealthApis',
        subject_token: azureToken,
        subject_token_type: 'urn:ietf:params:oauth:token-type:id_token',
        actor_token_type: 'urn:ietf:params:oauth:token-type:id_token'
      }
    });

    let paramString = postParams.toString();
    let stsEndpoint = environment.stsUrl;
    let postOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      context: new HttpContext().set(BYPASS_LOG, true)
    };

    return this.http.post<StsTokenOutput>(stsEndpoint, paramString, postOptions);
  }

  logout(token: string | null): Observable<any> {
    const url = API_BASE_URL + 'logout';
    const headers = new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : ''
    });
    return this.http.post(url, {}, { headers });
  }
}
